import type { EventCard, FileView, Thing } from "./parse.js";

// The project map (decision 26): a student-sized PRD built from PROJECT.md plus the code parse.
// PROJECT.md stays plain Markdown the student can edit; the map reads a few light conventions:
//
//   # Dodge the falling blocks            (optional title)
//   ## About / ## Plan
//   For: my little brother, who likes quick games
//   - [x] Player moves left and right    (MVP checklist)
//   Later: high score, speed-up, sound
//   ## Screens
//   - [x] Playing: player, falling blocks -> Game over when a block hits you
//   - [ ] Game over: score, "press R to restart"
//   ## Tests
//   - [x] The player can't leave the screen
//   ## Dream                            (the no-limits list, sorted by the helper)
//   - [ ] Today: player moves
//   This week: a score · Someday: a boss · Needs a grown-up: a real leaderboard

export interface ChecklistItem {
  text: string;
  done: boolean;
}

export interface Screen {
  name: string;
  /** What's on it. */
  shows: string;
  built: boolean;
  /** Where it goes next, and when. */
  next?: { to: string; when: string };
}

/** One wish from ## Dream, e.g. "- [ ] Today: player moves". */
export interface DreamItem {
  text: string;
  done: boolean;
}

/** The Impossible List: the no-limits version of the project, sorted into four groups. */
export interface Dream {
  today: DreamItem[];
  week: DreamItem[];
  someday: DreamItem[];
  grownup: DreamItem[];
  /** Plain wishes under ## Dream that don't have a label yet, so a re-sort still sees them. */
  unsorted: string[];
}

export interface ProjectPlan {
  title: string | null;
  about: string;
  forWhom: string | null;
  mvp: ChecklistItem[];
  later: string[];
  screens: Screen[];
  tests: ChecklistItem[];
  rules: string[];
  dream: Dream;
}

const section = (md: string, name: string): string => {
  const re = new RegExp(`^##\\s+${name}\\s*$([\\s\\S]*?)(?=^##\\s|(?![\\s\\S]))`, "im");
  return (re.exec(md)?.[1] ?? "").replace(/<!--[\s\S]*?-->/g, "").trim();
};

const checklist = (text: string): ChecklistItem[] =>
  text
    .split("\n")
    .map((l) => /^\s*[-*]\s+\[([ xX])\]\s+(.+)$/.exec(l))
    .filter((m): m is RegExpExecArray => !!m)
    .map((m) => ({ done: m[1] !== " ", text: m[2]!.trim() }));

// "- [ ] Today: player moves", "This week: ...", "Someday: ...", "Needs a grown-up: ..." (or "Needs a grown-up's help").
const DREAM_LINE = /^\s*(?:[-*]\s+)?(?:\[([ xX])\]\s+)?(?:\*\*)?(today|this week|someday|needs a grown-up(?:['’]s help)?)(?:\*\*)?\s*:\s*(?:\*\*)?\s*(.*)$/i;

export function parseDream(md: string): Dream {
  const dream: Dream = { today: [], week: [], someday: [], grownup: [], unsorted: [] };
  for (const raw of section(md, "Dream").split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    const m = DREAM_LINE.exec(raw);
    if (!m) {
      const text = line.replace(/^[-*]\s+(\[[ xX]\]\s+)?/, "").trim();
      if (text) dream.unsorted.push(text);
      continue;
    }
    const text = m[3]!.trim();
    if (!text) continue;
    const label = m[2]!.toLowerCase();
    const item = { text, done: m[1] !== undefined && m[1] !== " " };
    if (label === "today") dream.today.push(item);
    else if (label === "this week") dream.week.push(item);
    else if (label === "someday") dream.someday.push(item);
    else dream.grownup.push(item);
  }
  return dream;
}

export function parseProjectNotes(md: string): ProjectPlan {
  const title = /^#\s+(?!PROJECT\.md\s*$)(.+)$/m.exec(md)?.[1]?.trim() ?? null;
  const about = section(md, "About");
  const plan = section(md, "Plan");
  const both = `${about}\n${plan}`;
  // A starter's placeholder like "For: (who is this for?)" counts as not filled in yet.
  const forLine = /^\s*(?:\*\*)?For(?:\*\*)?\s*:\s*(?:\*\*)?\s*(.+)$/im.exec(both)?.[1]?.trim() ?? null;
  const forWhom = forLine && !/^\(.*\)$/.test(forLine) ? forLine : null;
  const later = (/^\s*(?:\*\*)?Later(?:\*\*)?\s*:\s*(?:\*\*)?\s*(.+)$/im.exec(both)?.[1] ?? "")
    .split(/\s*[,·;]\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
  const screens = section(md, "Screens")
    .split("\n")
    .map((l) => /^\s*[-*]\s+(?:\[([ xX])\]\s+)?([^:]+?)\s*(?::\s*(.*))?$/.exec(l))
    .filter((m): m is RegExpExecArray => !!m)
    .map((m) => {
      const [shows, next] = (m[3] ?? "").split(/\s*(?:->|→)\s*/);
      const nm = next ? /^(.+?)\s+(?:when|after|if)\s+(.+)$/i.exec(next) : null;
      return {
        name: m[2]!.trim(),
        shows: (shows ?? "").trim(),
        built: m[1] !== undefined && m[1] !== " ",
        next: next ? { to: (nm?.[1] ?? next).trim(), when: (nm?.[2] ?? "").trim() } : undefined,
      };
    });
  return {
    title,
    about: about.split("\n").filter((l) => !/^\s*(?:\*\*)?(For|Later)(?:\*\*)?\s*:/i.test(l)).join("\n").trim(),
    forWhom,
    mvp: checklist(plan),
    later,
    screens,
    tests: checklist(section(md, "Tests")),
    rules: section(md, "Rules")
      .split("\n")
      .map((l) => l.replace(/^\s*[-*]\s+/, "").trim())
      .filter(Boolean),
    dream: parseDream(md),
  };
}

export interface DataEntry {
  name: string;
  file: string;
  /** Plain-English description. */
  about: string;
  remembered: boolean;
  /** Titles of the cards that change it. */
  changedBy: string[];
}

export interface MapRule {
  title: string;
  does: string;
  built: boolean;
  cardId?: string;
}

export interface ArtItem {
  name: string;
  how: string;
}

export interface ProjectMap {
  plan: ProjectPlan;
  screens: Screen[];
  rules: MapRule[];
  data: DataEntry[];
  art: ArtItem[];
}

const KIND_ABOUT: Record<Thing["kind"], string> = {
  number: "a number",
  string: "some text",
  boolean: "yes or no",
  element: "something on the page",
  elements: "a group of things on the page",
  list: "a list",
  object: "a group of facts",
  drawing: "the pen that draws on the canvas",
  function: "a recipe",
  other: "a value",
  none: "starts empty",
};

const ASSET = /\.(png|jpe?g|gif|webp|svg|mp3|wav|ogg)$/i;
const EMOJI = /\p{Extended_Pictographic}/u;

export function buildProjectMap(files: Record<string, string>, views: FileView[]): ProjectMap {
  const plan = parseProjectNotes(files["PROJECT.md"] ?? "");
  const cards = views.flatMap((v) => v.cards);
  const eventCards = cards.filter((c) => c.kind !== "start" && c.kind !== "recipe");
  const saved = new Set(views.flatMap((v) => v.savedKeys));

  const rules: MapRule[] = eventCards.map((c) => ({ title: c.title, does: c.steps[0] ?? "nothing yet", built: true, cardId: c.id }));
  const screens = plan.screens.length
    ? plan.screens
    : [{ name: "Playing", shows: eventCards.length ? "what your cards do" : "nothing yet", built: cards.length > 0 }];
  // A planned move between screens is a rule that isn't built until the target screen is.
  for (const s of screens) {
    if (s.next?.when && !screens.find((t) => t.name.toLowerCase() === s.next!.to.toLowerCase())?.built) {
      rules.push({ title: `When ${s.next.when}`, does: `go to ${s.next.to}`, built: false });
    }
  }

  const data: DataEntry[] = views.flatMap((v) =>
    v.things
      .filter((t) => t.kind !== "drawing")
      .map((t) => {
        const changers = cards.filter((c) => c.kind !== "start" && c.changes.includes(t.name));
        // Name the event cards; a recipe only when no event card uses it.
        const events = changers.filter((c) => c.kind !== "recipe");
        const changedBy = (events.length ? events : changers).map((c) => c.title);
        const remembered = saved.has(t.name);
        const start = t.kind === "number" || t.kind === "string" || t.kind === "boolean" ? `, starts at ${t.init}` : "";
        return {
          name: t.name,
          file: t.file,
          about: `${KIND_ABOUT[t.kind]}${start}`,
          remembered,
          changedBy,
        };
      }),
  );
  for (const key of saved) if (!data.some((d) => d.name === key)) data.push({ name: key, file: "", about: "saved with saveData", remembered: true, changedBy: [] });

  const art: ArtItem[] = Object.keys(files)
    .filter((f) => ASSET.test(f))
    .map((f) => ({ name: f, how: /\.(mp3|wav|ogg)$/i.test(f) ? "sound file" : "picture file" }));
  const code = Object.entries(files)
    .filter(([f]) => /\.(js|html)$/i.test(f))
    .map(([, c]) => c)
    .join("\n");
  const emoji = [...new Set([...code.matchAll(/\p{Extended_Pictographic}️?/gu)].map((m) => m[0]))].filter((e) => EMOJI.test(e)).slice(0, 6);
  for (const e of emoji) art.push({ name: e, how: "emoji in your code" });
  if (/\.getContext\(\s*["']2d["']\s*\)/.test(code)) art.push({ name: "Shapes", how: "drawn by code" });

  return { plan, screens, rules, data, art };
}

/** Cards a picked card's run would show, for linking map rules back to Build. */
export function cardById(views: FileView[], id: string): EventCard | undefined {
  return views.flatMap((v) => v.cards).find((c) => c.id === id);
}

export interface ApprovedPlan {
  forWhom?: string;
  /** The plan's steps; they become the MVP checklist. */
  mvp: string[];
  later: string[];
  screens: { name: string; shows: string; next?: string }[];
  tests: string[];
}

/** Writes an approved plan into PROJECT.md in the conventions parseProjectNotes reads (decision 33). */
export function writePlanToNotes(md: string, plan: ApprovedPlan): string {
  const clean = (s: string) => s.replace(/[\r\n]+/g, " ").trim();
  let out = md.trim() ? md : "# PROJECT.md\n\n## About\n\n## Plan\n\n## Rules\n\n## Tests\n\n## Log\n";
  const setSection = (name: string, body: string, after?: string) => {
    const re = new RegExp(`(^##\\s+${name}\\s*$)([\\s\\S]*?)(?=^##\\s|(?![\\s\\S]))`, "im");
    if (re.test(out)) {
      out = out.replace(re, (_m, head: string) => `${head}\n${body}\n\n`);
      return;
    }
    const at = after ? new RegExp(`^##\\s+${after}\\s*$[\\s\\S]*?(?=^##\\s|(?![\\s\\S]))`, "im").exec(out) : null;
    const block = `## ${name}\n${body}\n\n`;
    out = at ? out.slice(0, at.index + at[0].length) + block + out.slice(at.index + at[0].length) : `${out.trimEnd()}\n\n${block}`;
  };
  const section = (name: string) => new RegExp(`^##\\s+${name}\\s*$([\\s\\S]*?)(?=^##\\s|(?![\\s\\S]))`, "im").exec(out)?.[1] ?? "";

  if (plan.forWhom) {
    const about = section("About").replace(/<!--[\s\S]*?-->/g, "");
    const lines = about.split("\n").filter((l) => l.trim() && !/^\s*(?:\*\*)?For(?:\*\*)?\s*:/i.test(l));
    setSection("About", [...lines, `For: ${clean(plan.forWhom)}`].join("\n"));
  }
  if (plan.mvp.length || plan.later.length) {
    const body = [...plan.mvp.map((s) => `- [ ] ${clean(s)}`), ...(plan.later.length ? [`Later: ${plan.later.map(clean).join(", ")}`] : [])].join("\n");
    setSection("Plan", body, "About");
  }
  if (plan.screens.length) {
    // Merge by name: planned screens replace same-named lines, other screens (like a site's Home and
    // About) stay, and a starter placeholder like "(your next page)" goes.
    const planned = new Map(plan.screens.map((x) => [clean(x.name).toLowerCase(), `- [ ] ${clean(x.name)}: ${clean(x.shows)}${x.next ? ` -> ${clean(x.next)}` : ""}`]));
    const kept = section("Screens")
      .replace(/<!--[\s\S]*?-->/g, "")
      .split("\n")
      .filter((l) => l.trim())
      .map((l) => {
        const name = /^\s*[-*]\s+(?:\[[ xX]\]\s+)?([^:]+?)\s*(?::|$)/.exec(l)?.[1]?.trim() ?? "";
        if (name.startsWith("(")) return null;
        const p = planned.get(name.toLowerCase());
        if (p) planned.delete(name.toLowerCase());
        return p ?? l;
      })
      .filter((l): l is string => l !== null);
    setSection("Screens", [...kept, ...planned.values()].join("\n"), "Plan");
  }
  if (plan.tests.length) {
    const existing = section("Tests").replace(/<!--[\s\S]*?-->/g, "").trim();
    const have = new Set(checklist(existing).map((t) => t.text.toLowerCase()));
    const add = plan.tests.map(clean).filter((t) => !have.has(t.toLowerCase())).map((t) => `- [ ] ${t}`);
    setSection("Tests", [existing, ...add].filter(Boolean).join("\n"));
  }
  return out.replace(/\n{3,}/g, "\n\n");
}

/** Ticks (or unticks) tests in PROJECT.md by their text, e.g. after "Check my tests". */
export function setTestsDone(md: string, results: { text: string; done: boolean }[]): string {
  const want = new Map(results.map((r) => [r.text.trim().toLowerCase(), r.done]));
  return md.replace(/^(\s*[-*]\s+\[)([ xX])(\]\s+)(.+)$/gm, (line, a: string, _mark: string, b: string, text: string) => {
    const done = want.get(text.trim().toLowerCase());
    return done === undefined ? line : `${a}${done ? "x" : " "}${b}${text}`;
  });
}
